import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAppSelector } from "hooks/reduxHooks";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Rating,
    Stack,
    Paper,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StarIcon from "@mui/icons-material/Star";
import CardRating from "./CardRating";

type RatingItem = {
    id: string;
    restaurantId?: string;
    restaurantName?: string;
    score: number;
    comment?: string;
    createdAt?: string;
    [key: string]: any;
};

const mockRatings: RatingItem[] = [
    {
        id: "r1",
        restaurantId: "REST001",
        restaurantName: "La Buena Mesa",
        score: 5,
        comment: "Excelente servicio y comida deliciosa.",
        createdAt: "2025-11-26T15:24:00Z",
    },
    {
        id: "r2",
        restaurantId: "REST002",
        restaurantName: "El Fogón",
        score: 4,
        comment: "Muy buen lugar, atención amable.",
        createdAt: "2025-11-25T11:12:00Z",
    },
    {
        id: "r3",
        restaurantId: "REST003",
        restaurantName: "Mar y Tierra",
        score: 3,
        comment: "Comida aceptable, tiempo de espera largo.",
        createdAt: "2025-11-24T18:05:00Z",
    },
    {
        id: "r4",
        restaurantId: "REST004",
        restaurantName: "La Esquina Verde",
        score: 2,
        comment: "Platos fríos y presentación pobre.",
        createdAt: "2025-11-23T09:30:00Z",
    },
    {
        id: "r5",
        restaurantId: "REST005",
        restaurantName: "Sabor Tradicional",
        score: 1,
        comment: "Muy mala experiencia, no regresaría.",
        createdAt: "2025-11-22T20:45:00Z",
    },
    {
        id: "r6",
        restaurantId: "REST006",
        restaurantName: "Café Central",
        score: 4,
        comment: "Buen café y ambiente agradable.",
        createdAt: "2025-11-21T08:10:00Z",
    },
];

const AllsRatings: React.FC = () => {
    const { token } = useAppSelector((s: any) => s.user);
    const [loading, setLoading] = useState<boolean>(false);
    const [ratings, setRatings] = useState<RatingItem[]>([]);

    const useMock = true;

    const fetchRatings = useCallback(
        async (signal?: AbortSignal) => {
            if (useMock) {
                setRatings(
                    [...mockRatings].sort((a, b) => {
                        const da = a.createdAt ? Date.parse(a.createdAt) : 0;
                        const db = b.createdAt ? Date.parse(b.createdAt) : 0;
                        return db - da;
                    })
                );
                return;
            }

            setLoading(true);
            try {
                const resp = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/ratings/all`, {
                    headers: { Authorization: `UserToken ${token}` },
                    signal,
                });
                const data: RatingItem[] = resp?.data?.data ?? resp?.data ?? [];
                const onlyRestaurants = data.filter(
                    (r) => r.score !== undefined && (r.restaurantId || r.restaurantName || r.targetType === "restaurant")
                );
                const sorted = onlyRestaurants.sort((a, b) => {
                    const da = a.createdAt ? Date.parse(a.createdAt) : 0;
                    const db = b.createdAt ? Date.parse(b.createdAt) : 0;
                    return db - da;
                });
                setRatings(sorted);
            } catch (err) {
                console.error("Error fetching ratings:", err);
                setRatings([]);
            } finally {
                setLoading(false);
            }
        },
        [token, useMock]
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchRatings(controller.signal);
        return () => controller.abort();
    }, [fetchRatings]);

    const stats = useMemo(() => {
        const totalReviews = ratings.length;
        const avgScore =
            totalReviews > 0 ? ratings.reduce((s, r) => s + (typeof r.score === "number" ? r.score : 0), 0) / totalReviews : 0;
        const avgRounded = Math.round(avgScore * 10) / 10;
        const uniqueVisited = new Set(ratings.map((r) => r.restaurantId ?? r.restaurantName ?? r.id)).size;
        const reviewsWith4Plus = ratings.filter((r) => (r.score ?? 0) >= 4).length;
        const lastUpdate = ratings
            .map((r) => (r.createdAt ? Date.parse(r.createdAt) : 0))
            .reduce((a, b) => Math.max(a, b), 0);
        const lastUpdatedAt = lastUpdate ? new Date(lastUpdate).toLocaleString() : null;

        return {
            totalReviews,
            avgRounded,
            uniqueVisited,
            reviewsWith4Plus,
            lastUpdatedAt,
        };
    }, [ratings]);

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <Box sx={{ maxWidth: 1400, mx: "auto" }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                        Todas las calificaciones
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Listado ordenado por fecha (más recientes primero).
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid size={12}>
                        <Card elevation={2}>
                            <CardContent>
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                                    <Box>
                                        <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                                            Promedio de estrellas
                                        </Typography>
                                        <Typography variant="h4" sx={{ color: "primary.main", my: 1 }}>
                                            {stats.avgRounded}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            sobre 5 — {stats.totalReviews} reseñas
                                        </Typography>
                                    </Box>
                                    <Rating value={stats.avgRounded} readOnly size="small" />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={12}>
                        <Card elevation={2}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <RestaurantIcon sx={{ color: "primary.main" }} />
                                        <Typography color="text.secondary" variant="subtitle2">
                                            Clientes visitados
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h4" sx={{ color: "primary.main" }}>
                                        {stats.uniqueVisited}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Restaurantes únicos
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={12}>
                        <Card elevation={2}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <TrendingUpIcon sx={{ color: "success.main" }} />
                                        <Typography color="text.secondary" variant="subtitle2">
                                            Reseñas destacadas
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h4" sx={{ color: "success.main" }}>
                                        {stats.reviewsWith4Plus}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        4+ estrellas
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={12}>
                        <Card elevation={2}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <StarIcon sx={{ color: "warning.main" }} />
                                        <Typography color="text.secondary" variant="subtitle2">
                                            Total reseñas
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h4" sx={{ color: "warning.main" }}>
                                        {stats.totalReviews}
                                    </Typography>
                                    {stats.lastUpdatedAt && (
                                        <Typography variant="caption" color="text.secondary">
                                            Última: {stats.lastUpdatedAt}
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Ratings Grid */}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : ratings.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <Typography color="text.secondary">No se encontraron calificaciones.</Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={2}>
                        {ratings.map((r) => (
                            <Grid size={12} key={r.id}>
                                <CardRating
                                    id={r.id}
                                    cardCode={r.restaurantId ?? ""}
                                    cardName={r.restaurantName ?? ""}
                                    comment={r.comment ?? ""}
                                    score={r.score ?? 0}
                                    createdAt={r.createdAt ?? ""}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default AllsRatings;